import { Lock, RectangleHorizontal, RotateCcw, Square, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { ApartmentAverageForProject } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import { asId, type ProjectId, type StructuralPieceId } from '@obraplan/core/domain/shared/ids';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import {
  groundBeamFreeLength,
  groundBeamVolume,
  pileCapBottom,
  pileCapVolume,
  type GroundBeam,
  type PileCap,
} from '@obraplan/core/domain/takeoff/foundation';
import type { ItemFactor } from '@obraplan/core/domain/takeoff/own-factor';
import {
  checkPileCapTypeFields,
  PILE_CAP_TYPE_NAME_MAX,
  type PileCapType,
} from '@obraplan/core/domain/takeoff/pile-cap-type';
import {
  nomeDaPeca,
  pecasImportadas,
  type QuantidadesPorPeca,
} from '@obraplan/core/domain/takeoff/planilha-por-peca';
import type { Point } from '@obraplan/core/domain/takeoff/polygon';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import {
  STEEL_GAUGES,
  totalSteelRate,
  type SteelGauge,
  type SteelRate,
} from '@obraplan/core/domain/takeoff/steel';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { NOME_DA_PECA_MAX, NomeInput, NumeroInput } from './campos';
import {
  fatoresDaPecaDaFundacao,
  fatoresDoBloco,
  mediaDoAcoDaPeca,
  taxaDeBaseDaPeca,
  type MediaAoLado,
  type TaxaDeBase,
} from './fatores-proprios';
import { AMediaDoTipoMudou, FatoresProprios, type AplicarAoTipo } from './FatoresProprios';
import {
  centroDoContorno,
  comprimentoDoEixo,
  COR_DO_BALDRAME,
  COR_DO_BLOCO,
  encaixarNoBloco,
  faixaDoEixo,
  pontosDe,
  rotuloDoBaldrame,
  secaoDoBaldrame,
  trechosDoBaldrame,
} from './fundacao-da-prancha';
import { metros, paraNumero, paraTexto } from './formato';
import {
  atualizarTipoPelaMedia,
  mediaDoTipoNovo,
  mudancasDoTipo,
  semNadaNoTipo,
} from './media-no-tipo';
import { metresToPixel, pixelToMetres, type Pixel } from './plan-tracer';
import {
  aprovacaoDe,
  nomeDoAprovado,
  type AprovacaoDoServico,
  type ServicosAprovados,
} from './servicos-aprovados';
import type {
  ArrastoDaFundacao,
  FerramentaDaFundacao,
  FormatoDoBloco,
  FundacaoNaPrancha,
  PecaDaFundacao,
} from './use-fundacao-na-prancha';
import type { WorkspaceAction } from './typology-draft';

/**
 * O bloco e a viga baldrame sobre a prancha, e os painéis deles (Fase 27, etapa 2).
 *
 * O bloco é o contorno cheio, com o nome no meio. O baldrame é a faixa **na largura da
 * seção** ao longo do eixo, com o nome e o comprimento livre no rótulo; o trecho que passa
 * dentro de um bloco sai em traço mais claro, para quem desenha ver o que não conta (RN-368).
 * As cores não seguem o tema, pelo mesmo motivo das do resto do traçado: a prancha é papel
 * branco.
 */

// ── O desenho ───────────────────────────────────────────────────────────────────

/** Um baldrame: a faixa com o trecho dentro dos blocos mais claro, e o rótulo com o livre. */
function BaldrameNaPrancha({
  baldrame,
  blocos,
  escala,
  zoom,
  selecionado,
  pontas,
  novo,
}: {
  baldrame: Pick<GroundBeam, 'name' | 'from' | 'to' | 'width' | 'height'> & { id?: string };
  blocos: readonly PileCap[];
  escala: number;
  zoom: number;
  selecionado: boolean;
  /** As alças das duas pontas, para arrastar — só no baldrame escolhido. */
  pontas: boolean;
  /** O baldrame ainda não cadastrado: tracejado, e sem alvo para clique. */
  novo: boolean;
}) {
  const de = metresToPixel(baldrame.from, escala);
  const ate = metresToPixel(baldrame.to, escala);
  const largura = Math.max(baldrame.width / escala, 3 / zoom);
  const trechos = trechosDoBaldrame(baldrame, blocos);
  const meio = { x: (de.x + ate.x) / 2, y: (de.y + ate.y) / 2 };
  const fonte = 11 / zoom;
  const rotulo = rotuloDoBaldrame(baldrame, blocos);
  const ponto = (t: number): Pixel => ({
    x: de.x + (ate.x - de.x) * t,
    y: de.y + (ate.y - de.y) * t,
  });

  return (
    <g
      data-baldrame={novo ? undefined : baldrame.id}
      data-baldrame-novo={novo ? '' : undefined}
      className={novo ? 'pointer-events-none' : undefined}
    >
      <title>
        {`Baldrame ${baldrame.name} — ${secaoDoBaldrame(baldrame)} cm · eixo ${metros(
          comprimentoDoEixo(baldrame),
        )} m · livre ${metros(groundBeamFreeLength(baldrame, blocos))} m`}
      </title>
      {/* O alvo do clique: mais largo que a faixa, que numa seção de 20 cm é fina demais. */}
      <line
        x1={de.x}
        y1={de.y}
        x2={ate.x}
        y2={ate.y}
        stroke="transparent"
        strokeWidth={Math.max(largura, 12 / zoom)}
        pointerEvents="stroke"
      />
      {trechos.map((trecho) => {
        const a = ponto(trecho.de);
        const b = ponto(trecho.ate);
        return (
          <line
            key={`${String(trecho.de)}-${String(trecho.ate)}`}
            data-trecho={trecho.dentro ? 'dentro' : 'livre'}
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            stroke={COR_DO_BALDRAME}
            strokeOpacity={trecho.dentro ? 0.2 : novo ? 0.5 : 0.7}
            strokeWidth={largura}
            strokeDasharray={novo ? `${4 / zoom} ${3 / zoom}` : undefined}
            pointerEvents="none"
          />
        );
      })}
      {selecionado && (
        <polygon
          points={pontosDe(faixaDoEixo(de, ate, largura))}
          fill="none"
          stroke={COR_DO_BALDRAME}
          strokeWidth={3 / zoom}
          pointerEvents="none"
        />
      )}
      <text
        data-rotulo=""
        x={meio.x}
        y={meio.y - largura / 2 - 4 / zoom}
        textAnchor="middle"
        fontSize={fonte}
        fontWeight={600}
        fill="#4c1d95"
        stroke="#ffffff"
        strokeWidth={fonte * 0.22}
        paintOrder="stroke"
        className="pointer-events-none select-none"
      >
        {rotulo}
      </text>
      {pontas &&
        (['de', 'ate'] as const).map((qual) => {
          const lugar = qual === 'de' ? de : ate;
          return (
            <circle
              key={qual}
              data-baldrame-ponta={baldrame.id}
              data-ponta={qual}
              cx={lugar.x}
              cy={lugar.y}
              r={6 / zoom}
              fill="#ffffff"
              stroke={COR_DO_BALDRAME}
              strokeWidth={2 / zoom}
              className="cursor-move"
            />
          );
        })}
    </g>
  );
}

export function FundacaoDesenhada({
  blocos,
  baldrames,
  escala,
  zoom,
  selecionada,
  arrastando,
  blocosClicaveis,
  baldramesClicaveis,
  onClicarBloco,
  onClicarBaldrame,
}: {
  blocos: readonly PileCap[];
  baldrames: readonly GroundBeam[];
  escala: number;
  zoom: number;
  selecionada: PecaDaFundacao | undefined;
  arrastando: ArrastoDaFundacao | undefined;
  /** Ausente enquanto se traça outra coisa: aí o clique é do traço, e o bloco deixa passar. */
  blocosClicaveis: boolean;
  baldramesClicaveis: boolean;
  onClicarBloco: (id: StructuralPieceId) => void;
  onClicarBaldrame: (id: StructuralPieceId) => void;
}) {
  if (blocos.length === 0 && baldrames.length === 0) return null;

  return (
    <g>
      <g className={blocosClicaveis ? 'cursor-pointer' : 'pointer-events-none'}>
        {blocos.map((bloco) => {
          const naMao =
            arrastando?.tipo === 'BLOCO' && arrastando.id === bloco.id && arrastando.moveu;
          const dx = naMao ? arrastando.atual.x - arrastando.origem.x : 0;
          const dy = naMao ? arrastando.atual.y - arrastando.origem.y : 0;
          const deslocar = (ponto: Point): Pixel => {
            const pixel = metresToPixel(ponto, escala);
            return { x: pixel.x + dx, y: pixel.y + dy };
          };
          const centro = deslocar(centroDoContorno(bloco.outline));
          const escolhido = selecionada?.tipo === 'BLOCO' && selecionada.id === bloco.id;
          const fonte = 12 / zoom;

          return (
            <g
              key={bloco.id}
              data-bloco={bloco.id}
              onClick={(evento) => {
                if (!blocosClicaveis) return;
                evento.stopPropagation();
                onClicarBloco(bloco.id);
              }}
            >
              <title>
                {`Bloco ${bloco.name} — ${metros(pileCapBottom(bloco))} m² × ${metros(bloco.height)} m`}
              </title>
              <polygon
                points={pontosDe(bloco.outline.map(deslocar))}
                fill={COR_DO_BLOCO}
                fillOpacity={0.28}
                stroke={COR_DO_BLOCO}
                strokeWidth={(escolhido ? 4 : 1.5) / zoom}
                strokeLinejoin="round"
              />
              <text
                x={centro.x}
                y={centro.y}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={fonte}
                fontWeight={600}
                fill="#134e4a"
                stroke="#ffffff"
                strokeWidth={fonte * 0.22}
                paintOrder="stroke"
                className="pointer-events-none select-none"
              >
                {bloco.name}
              </text>
            </g>
          );
        })}
      </g>

      <g className={baldramesClicaveis ? 'cursor-pointer' : 'pointer-events-none'}>
        {baldrames.map((baldrame) => {
          const escolhido = selecionada?.tipo === 'BALDRAME' && selecionada.id === baldrame.id;
          // A ponta na mão anda com o mouse, e o encaixe no centro do bloco já aparece.
          const naMao =
            arrastando?.tipo === 'PONTA' && arrastando.id === baldrame.id && arrastando.moveu
              ? arrastando
              : undefined;
          const solta =
            naMao === undefined
              ? undefined
              : encaixarNoBloco(pixelToMetres(naMao.atual, escala), blocos);
          const quando: GroundBeam =
            naMao === undefined || solta === undefined
              ? baldrame
              : naMao.ponta === 'de'
                ? { ...baldrame, from: solta }
                : { ...baldrame, to: solta };

          return (
            <g
              key={baldrame.id}
              onClick={(evento) => {
                if (!baldramesClicaveis) return;
                evento.stopPropagation();
                onClicarBaldrame(baldrame.id);
              }}
            >
              <BaldrameNaPrancha
                baldrame={quando}
                blocos={blocos}
                escala={escala}
                zoom={zoom}
                selecionado={escolhido}
                pontas={escolhido && baldramesClicaveis}
                novo={false}
              />
            </g>
          );
        })}
      </g>
    </g>
  );
}

/** O que está sendo traçado e ainda não foi cadastrado: o bloco em contorno, o eixo em faixa. */
export function FundacaoEmTraco({
  fundacao,
  blocos,
  escala,
  zoom,
}: {
  fundacao: FundacaoNaPrancha;
  blocos: readonly PileCap[];
  escala: number;
  zoom: number;
}) {
  const { traco, eixo, cursor, eixoEmMetros } = fundacao;

  if (fundacao.ferramenta === 'BLOCO' && traco !== undefined) {
    return (
      <g className="pointer-events-none">
        {traco.fechado && traco.vertices.length >= 3 ? (
          <polygon
            data-bloco-novo=""
            points={pontosDe(traco.vertices)}
            fill={COR_DO_BLOCO}
            fillOpacity={0.15}
            stroke={COR_DO_BLOCO}
            strokeWidth={2 / zoom}
            strokeDasharray={`${4 / zoom} ${3 / zoom}`}
          />
        ) : (
          <polyline
            data-bloco-novo=""
            points={pontosDe(
              traco.ao === undefined ? traco.vertices : [...traco.vertices, traco.ao],
            )}
            fill="none"
            stroke={COR_DO_BLOCO}
            strokeWidth={2 / zoom}
            strokeDasharray={`${4 / zoom} ${3 / zoom}`}
          />
        )}
        {traco.marcados.map((ponto, indice) => (
          <circle
            key={`b-${String(indice)}`}
            cx={ponto.x}
            cy={ponto.y}
            r={(indice === 0 ? 6 : 4) / zoom}
            fill={COR_DO_BLOCO}
          />
        ))}
      </g>
    );
  }

  if (fundacao.ferramenta === 'BALDRAME' && eixo.length > 0) {
    const primeiro = eixo[0]!;
    const vivo =
      cursor === undefined
        ? undefined
        : metresToPixel(encaixarNoBloco(pixelToMetres(cursor, escala), blocos), escala);
    return (
      <g className="pointer-events-none">
        {eixoEmMetros !== undefined ? (
          <BaldrameNaPrancha
            baldrame={{
              name: fundacao.nomeDoBaldrame,
              from: eixoEmMetros.from,
              to: eixoEmMetros.to,
              width: fundacao.larguraEmCm / 100,
              height: fundacao.alturaEmCm / 100,
            }}
            blocos={blocos}
            escala={escala}
            zoom={zoom}
            selecionado={false}
            pontas={false}
            novo
          />
        ) : (
          vivo !== undefined && (
            <line
              data-baldrame-novo=""
              x1={primeiro.x}
              y1={primeiro.y}
              x2={vivo.x}
              y2={vivo.y}
              stroke={COR_DO_BALDRAME}
              strokeWidth={2 / zoom}
              strokeDasharray={`${4 / zoom} ${3 / zoom}`}
            />
          )
        )}
        {eixo.map((ponto, indice) => (
          <circle
            key={`e-${String(indice)}`}
            cx={ponto.x}
            cy={ponto.y}
            r={5 / zoom}
            fill={COR_DO_BALDRAME}
          />
        ))}
      </g>
    );
  }

  return null;
}

// ── Os botões da barra ──────────────────────────────────────────────────────────

/** "Bloco" e "Baldrame": só existem onde a estaca existe — na Fundação e na Contenção. */
export function BotoesDaFundacao({
  ferramenta,
  onEscolher,
}: {
  ferramenta: FerramentaDaFundacao | undefined;
  onEscolher: (ferramenta: FerramentaDaFundacao) => void;
}) {
  return (
    <>
      <Button
        size="sm"
        variant={ferramenta === 'BLOCO' ? 'primary' : 'ghost'}
        onClick={() => onEscolher('BLOCO')}
      >
        <Square className="size-4" /> Bloco
      </Button>
      <Button
        size="sm"
        variant={ferramenta === 'BALDRAME' ? 'primary' : 'ghost'}
        onClick={() => onEscolher('BALDRAME')}
      >
        <RectangleHorizontal className="size-4" /> Baldrame
      </Button>
    </>
  );
}

// ── Os painéis da ferramenta ────────────────────────────────────────────────────

const ROTULO_DO_FORMATO: Readonly<Record<FormatoDoBloco, string>> = {
  RETANGULO: 'Retângulo',
  POLIGONO: 'Polígono',
};

export function EmBloco({
  fundacao,
  erro,
  onSair,
}: {
  fundacao: FundacaoNaPrancha;
  /** A última recusa do desenho — o bloco arrastado para onde não pode, por exemplo. */
  erro: string | undefined;
  onSair: () => void;
}) {
  const { formato, contorno, cantos } = fundacao;
  const aberto = formato === 'POLIGONO' && !fundacao.fechado;

  return (
    <section
      aria-label="Blocos"
      className="flex flex-col gap-3 rounded-md border border-(--color-brand) p-3"
    >
      <h3 className="text-sm font-semibold">Bloco</h3>

      <div role="group" aria-label="Forma do bloco" className="flex flex-wrap gap-1">
        {(['RETANGULO', 'POLIGONO'] as const).map((item) => (
          <Button
            key={item}
            size="sm"
            variant={formato === item ? 'primary' : 'ghost'}
            aria-pressed={formato === item}
            onClick={() => fundacao.escolherFormato(item)}
          >
            {ROTULO_DO_FORMATO[item]}
          </Button>
        ))}
      </div>

      <p className="text-xs text-(--color-content-muted)">
        {formato === 'RETANGULO'
          ? 'Arraste de um canto ao outro do bloco — ou clique nos dois cantos. O bloco de três estacas não é retângulo: use o polígono.'
          : 'Clique os vértices do contorno e feche no primeiro ponto — ou em "Fechar contorno".'}
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <Field label="Nome do bloco" htmlFor="bloco-nome">
          <Input
            id="bloco-nome"
            maxLength={NOME_DA_PECA_MAX}
            value={fundacao.nomeDoBloco}
            className="w-28"
            onChange={(evento) => fundacao.digitarNomeDoBloco(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter') fundacao.adicionarBloco();
            }}
          />
        </Field>
        <Field label="Altura do bloco (m)" htmlFor="bloco-altura">
          <NumeroInput
            id="bloco-altura"
            value={fundacao.alturaDoBloco}
            className="w-24"
            onChange={fundacao.mudarAlturaDoBloco}
          />
        </Field>
      </div>

      <span className="text-xs text-(--color-content-muted)">
        {contorno === undefined
          ? formato === 'RETANGULO'
            ? `${String(cantos)} de 2 cantos`
            : aberto && cantos > 0
              ? `${String(cantos)} ${cantos === 1 ? 'ponto' : 'pontos'} no contorno`
              : 'Nenhum ponto marcado na planta.'
          : `Contorno pronto: ${metros(pileCapBottom({ outline: contorno }))} m² · concreto ${metros(
              pileCapVolume({ outline: contorno, height: fundacao.alturaDoBloco }),
              3,
            )} m³`}
      </span>
      {fundacao.impedimentoDoBloco !== undefined && (
        <span className="text-xs text-(--color-state-warn)">{fundacao.impedimentoDoBloco}</span>
      )}
      {fundacao.avisoDaAlturaDoBloco !== undefined && (
        <span className="text-xs text-(--color-state-warn)">{fundacao.avisoDaAlturaDoBloco}</span>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={contorno === undefined || fundacao.impedimentoDoBloco !== undefined}
          onClick={fundacao.adicionarBloco}
        >
          Adicionar bloco
        </Button>
        {formato === 'POLIGONO' && (
          <>
            <Button
              variant="secondary"
              size="sm"
              disabled={!aberto || cantos < 3}
              onClick={fundacao.fecharContorno}
            >
              Fechar contorno
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={cantos === 0}
              onClick={fundacao.desfazerPonto}
            >
              Desfazer ponto
            </Button>
          </>
        )}
        <Button variant="ghost" size="sm" disabled={cantos === 0} onClick={fundacao.refazerBloco}>
          Refazer
        </Button>
      </div>

      {erro !== undefined && (
        <p role="alert" className="text-xs text-(--color-state-danger)">
          {erro}
        </p>
      )}

      <div className="border-t border-(--color-border-subtle) pt-2">
        <Button variant="ghost" size="sm" onClick={onSair}>
          Sair
        </Button>
      </div>
    </section>
  );
}

export function EmBaldrame({
  fundacao,
  blocos,
  erro,
  onSair,
}: {
  fundacao: FundacaoNaPrancha;
  blocos: readonly PileCap[];
  erro: string | undefined;
  onSair: () => void;
}) {
  const { eixoEmMetros } = fundacao;

  return (
    <section
      aria-label="Baldrames"
      className="flex flex-col gap-3 rounded-md border border-(--color-brand) p-3"
    >
      <h3 className="text-sm font-semibold">Baldrame</h3>
      <p className="text-xs text-(--color-content-muted)">
        Clique nas duas pontas do eixo. O clique que cai dentro de um bloco vale o centro dele; o
        trecho que passa dentro do bloco não conta.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <Field label="Nome do baldrame" htmlFor="baldrame-nome">
          <Input
            id="baldrame-nome"
            maxLength={NOME_DA_PECA_MAX}
            value={fundacao.nomeDoBaldrame}
            className="w-28"
            onChange={(evento) => fundacao.digitarNomeDoBaldrame(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter') fundacao.adicionarBaldrame();
            }}
          />
        </Field>
        <Field label="Largura do baldrame (cm)" htmlFor="baldrame-largura">
          <NumeroInput
            id="baldrame-largura"
            value={fundacao.larguraEmCm}
            className="w-24"
            onChange={fundacao.mudarLarguraEmCm}
          />
        </Field>
        <Field label="Altura do baldrame (cm)" htmlFor="baldrame-altura">
          <NumeroInput
            id="baldrame-altura"
            value={fundacao.alturaEmCm}
            className="w-24"
            onChange={fundacao.mudarAlturaEmCm}
          />
        </Field>
      </div>

      <span className="text-xs text-(--color-content-muted)">
        {eixoEmMetros === undefined
          ? `${String(fundacao.eixo.length)} de 2 pontos`
          : `Seção ${secaoDoBaldrame({
              width: fundacao.larguraEmCm / 100,
              height: fundacao.alturaEmCm / 100,
            })} cm · eixo ${metros(comprimentoDoEixo(eixoEmMetros))} m · livre ${metros(
              groundBeamFreeLength(eixoEmMetros, blocos),
            )} m`}
      </span>
      {fundacao.impedimentoDoBaldrame !== undefined && (
        <span className="text-xs text-(--color-state-warn)">{fundacao.impedimentoDoBaldrame}</span>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={eixoEmMetros === undefined || fundacao.impedimentoDoBaldrame !== undefined}
          onClick={fundacao.adicionarBaldrame}
        >
          Adicionar baldrame
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={fundacao.eixo.length === 0}
          onClick={fundacao.refazerBaldrame}
        >
          Refazer
        </Button>
      </div>

      {erro !== undefined && (
        <p role="alert" className="text-xs text-(--color-state-danger)">
          {erro}
        </p>
      )}

      <div className="border-t border-(--color-border-subtle) pt-2">
        <Button variant="ghost" size="sm" onClick={onSair}>
          Sair
        </Button>
      </div>
    </section>
  );
}

// ── A peça selecionada ──────────────────────────────────────────────────────────

/** Metros em centímetros, com uma casa no máximo — "0,2" vira 20, e não 20,000000000000004. */
const emCentimetros = (valor: number): number => Math.round(valor * 1000) / 10;

/**
 * Número que só grava ao terminar de digitar — ao sair do campo ou no Enter.
 *
 * A altura do bloco vai a `UPDATE_PILE_CAP` e o domínio recusa zero: gravar a cada tecla
 * acusaria "maior que zero" no meio de "0,8", por causa de um zero que ninguém quis.
 */
export function NumeroAoTerminar({
  id,
  valor,
  onCommit,
  className,
}: {
  id: string;
  valor: number;
  onCommit: (valor: number) => void;
  className?: string;
}) {
  const [texto, setTexto] = useState(paraTexto(valor));
  const [focado, setFocado] = useState(false);

  useEffect(() => {
    if (!focado) setTexto(paraTexto(valor));
  }, [valor, focado]);

  const encerrar = (): void => {
    setFocado(false);
    const lido = paraNumero(texto);
    if (lido === undefined || lido === valor) setTexto(paraTexto(valor));
    else onCommit(lido);
  };

  return (
    <Input
      id={id}
      inputMode="decimal"
      value={texto}
      className={className}
      onFocus={() => setFocado(true)}
      onBlur={encerrar}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter') evento.currentTarget.blur();
      }}
      onChange={(evento) => setTexto(evento.target.value)}
    />
  );
}

export function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-(--color-content-muted)">{rotulo}</span>
      <strong className="tabular-nums">{valor}</strong>
    </div>
  );
}

/**
 * Os fatores próprios no card do bloco, do baldrame e do radier (Fase 41, RN-538, RN-540) — o
 * `FatoresProprios` da estaca, com `SET_FOUNDATION_FACTOR`. Desde a Fase 42 (etapa 3 — G3,
 * RN-571, RN-574), o da escada, do muro e do pilar de contenção também: sem tipo, sem "Aplicar
 * ao tipo".
 *
 * A peça da planilha por peça não leva fator: o card diz por quê (F10). O que ela já tinha de
 * antes ainda se tira — tirar continua valendo.
 */
export function FatoresDaPecaDaFundacao({
  peca,
  rotulo,
  fala,
  servicos,
  daPlanilha,
  semFator,
  rotuloDaVolta,
  dispatch,
  aplicarAoTipo,
}: {
  peca: { readonly id: StructuralPieceId; readonly factors?: readonly ItemFactor[] };
  /** "bloco", "baldrame", "radier", "escada", "muro", "pilar de contenção" — no título e no vazio. */
  rotulo: string;
  /** As palavras da peça feminina (a escada). Ausente, a masculina do rótulo. */
  fala?: FalaDaPeca | undefined;
  servicos: ReturnType<typeof fatoresDoBloco>;
  daPlanilha: boolean;
  /** O que a peça importada diz: "não leva tipo nem fator próprio". */
  semFator: string;
  rotuloDaVolta: string;
  dispatch: React.Dispatch<WorkspaceAction>;
  /** O link que leva o próprio para a composição do tipo de bloco (RN-536). Só o bloco. */
  aplicarAoTipo?: AplicarAoTipo | undefined;
}) {
  const palavras = falaInteira(rotulo, fala);
  const proprios = peca.factors ?? [];
  if (daPlanilha) {
    return (
      <div className="flex flex-col items-start gap-1 text-xs">
        <p className="text-(--color-content-muted)">
          {palavras.este} {rotulo} veio {palavras.pronto} da planilha por peça: a quantidade já vem
          pronta, e {palavras.ele} {semFator}.
        </p>
        {proprios.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              proprios.forEach((linha) =>
                dispatch({
                  type: 'SET_FOUNDATION_FACTOR',
                  pieceId: peca.id,
                  serviceKey: linha.serviceKey,
                  itemKey: linha.itemKey,
                }),
              )
            }
          >
            Tirar os fatores próprios
          </Button>
        )}
      </div>
    );
  }
  return (
    <FatoresProprios
      idBase={`${rotulo}-${peca.id}-fator`}
      titulo={`Fatores ${palavras.deste} ${rotulo}`}
      servicos={servicos}
      rotuloDaVolta={rotuloDaVolta}
      vazio={`Nenhum serviço da obra mede ${palavras.o} ${rotulo} com item a converter.`}
      onMudar={(serviceKey, itemKey, factor) =>
        dispatch({
          type: 'SET_FOUNDATION_FACTOR',
          pieceId: peca.id,
          serviceKey,
          itemKey,
          ...(factor === undefined ? {} : { factor }),
        })
      }
      aplicarAoTipo={aplicarAoTipo}
    />
  );
}

/**
 * O aço no card do bloco, do baldrame e do radier (Fase 41, etapa 2 — RN-543, RN-544, RN-547): o
 * recolhível `AcoDaPeca`, despachando a taxa própria da peça da fundação (`SET_PIECE_STEEL_RATES`).
 * Desde a Fase 42 (etapa 3 — RN-572, RN-574, RN-575), o da escada, do muro e do pilar de
 * contenção também, com a trava do serviço de aço aprovado (`travadaPor`).
 *
 * A peça da planilha por peça não tem o recolhível: o aço dela já veio pronto. A própria que ela
 * já tinha ainda se tira.
 */
export function AcoDaPecaDaFundacao({
  peca,
  rotulo,
  fala,
  base,
  daPlanilha,
  dispatch,
  aplicarAoTipo,
  media,
  travadaPor,
}: {
  peca: { readonly id: StructuralPieceId; readonly steelRates?: readonly SteelRate[] };
  /** "bloco", "baldrame", "radier", "escada", "muro", "pilar de contenção". */
  rotulo: string;
  /** As palavras da peça feminina (a escada). Ausente, a masculina do rótulo. */
  fala?: FalaDaPeca | undefined;
  /** A taxa que a peça segue sem a própria (`taxaDeBaseDaPeca`). */
  base: TaxaDeBase;
  daPlanilha: boolean;
  dispatch: React.Dispatch<WorkspaceAction>;
  /** O tipo de bloco para onde a própria pode ir (RN-543). Só o bloco com tipo. */
  aplicarAoTipo?: AplicarTaxaAoTipo | undefined;
  /** A média de hoje do aço do elemento, por bitola (`mediaDoAcoDaPeca`). Vazia ou ausente, nada. */
  media?: ReadonlyMap<SteelGauge, MediaAoLado> | undefined;
  /** Os serviços de aço aprovados que leem esta taxa (`acoAprovadoDoElemento`). Ausente = livre. */
  travadaPor?: readonly AprovacaoDoServico[] | undefined;
}) {
  const palavras = falaInteira(rotulo, fala);
  const propria = taxaPropria(peca.steelRates);
  const mudar = (rates: readonly SteelRate[]) =>
    dispatch({ type: 'SET_PIECE_STEEL_RATES', pieceId: peca.id, rates });

  if (daPlanilha) {
    return propria === undefined ? null : (
      <div className="flex flex-col items-start gap-1 text-xs">
        <p className="text-(--color-content-muted)">
          {palavras.este} {rotulo} veio {palavras.pronto} da planilha por peça: o aço já vem pronto,
          e {palavras.ele} não leva taxa própria.
        </p>
        <Button variant="ghost" size="sm" onClick={() => mudar([])}>
          Tirar a taxa de aço própria
        </Button>
      </div>
    );
  }

  return (
    <AcoDaPeca
      propria={propria}
      fala={palavras}
      base={base}
      onMudar={mudar}
      aplicarAoTipo={aplicarAoTipo}
      media={media}
      {...(travadaPor === undefined ? {} : { travadaPor })}
    />
  );
}

/** A lista própria que vale: a vazia é "nenhuma", e a peça segue a de baixo. */
export function taxaPropria(
  taxas: readonly SteelRate[] | undefined,
): readonly SteelRate[] | undefined {
  return taxas !== undefined && taxas.length > 0 ? taxas : undefined;
}

/** Levar a própria da peça para o tipo (RN-543, RN-567): o link diz para onde a lista vai. */
export interface AplicarTaxaAoTipo {
  readonly nome: string;
  readonly onAplicar: (taxas: readonly SteelRate[]) => void;
}

/** Como o recolhível fala da peça: "deste bloco", "desta viga" — o gênero é da peça. */
export interface FalaDaPeca {
  /** "bloco", "viga". */
  readonly nome: string;
  /** "deste", "desta". */
  readonly deste: string;
  /** "neste", "nesta". */
  readonly neste: string;
  /** "do", "da". */
  readonly do: string;
  /** "o", "a" — "mede o bloco", "mede a escada". Ausente: "o". */
  readonly o?: string;
  /** "Este", "Esta" — o começo da frase da peça importada. Ausente: "Este". */
  readonly este?: string;
  /** "pronto", "pronta" — "veio pronta da planilha". Ausente: "pronto". */
  readonly pronto?: string;
  /** "ele", "ela". Ausente: "ele". */
  readonly ele?: string;
}

/** A fala de uma peça masculina — "o bloco", "o muro" —, a partir do nome dela. */
export function falaMasculina(nome: string): Required<FalaDaPeca> {
  return {
    nome,
    deste: 'deste',
    neste: 'neste',
    do: 'do',
    o: 'o',
    este: 'Este',
    pronto: 'pronto',
    ele: 'ele',
  };
}

/** A fala de uma peça feminina — "a escada" —, a partir do nome dela. */
export function falaFeminina(nome: string): Required<FalaDaPeca> {
  return {
    nome,
    deste: 'desta',
    neste: 'nesta',
    do: 'da',
    o: 'a',
    este: 'Esta',
    pronto: 'pronta',
    ele: 'ela',
  };
}

/** A fala inteira: o que quem chama disse, por cima da masculina do rótulo. */
function falaInteira(rotulo: string, fala: FalaDaPeca | undefined): Required<FalaDaPeca> {
  return { ...falaMasculina(rotulo), ...fala };
}

/**
 * O recolhível do aço de uma peça (Fase 41, etapa 2 — RN-543, RN-544; Fase 42, etapa 2 — RN-567
 * a RN-570), como os fatores, com o kg/m³ de cada bitola. Serve ao bloco, ao baldrame e ao radier
 * (`AcoDaPecaDaFundacao`) e ao pilar e à viga (`PilarEVigaNaPrancha`): quem chama diz a taxa
 * própria, a de baixo e o que fazer com a lista nova.
 *
 * Sem a própria, os campos mostram a taxa que a peça segue — a do tipo ou a da disciplina — e de
 * onde ela vem. A lista vale inteira (ADR-187): mudar a primeira bitola cria a própria copiando a
 * de baixo e trocando só aquela. "Voltar" apaga a própria; "Aplicar ao tipo" leva a própria da
 * peça para o tipo, e a peça volta a lê-la de lá.
 *
 * Com a média do aço do elemento (Fase 41, etapa 3 — RN-554, F6), cada bitola mostra a de hoje ao
 * lado do campo. Só para ler: a taxa do tipo e a própria não mudam com ela (D8).
 *
 * Com um serviço de aço do elemento aprovado (`travadaPor`, RN-547, RN-570), a taxa só se lê: os
 * campos viram texto, e o "Voltar" e o "Aplicar ao tipo" somem — mudá-la mudaria a quantidade do
 * aprovado, e o servidor recusaria.
 */
export function AcoDaPeca({
  propria,
  fala,
  base,
  onMudar,
  aplicarAoTipo,
  media,
  travadaPor = [],
}: {
  /** A taxa própria da peça, já sem a lista vazia (`taxaPropria`). */
  propria: readonly SteelRate[] | undefined;
  fala: FalaDaPeca;
  /** A taxa que a peça segue sem a própria (`taxaDeBaseDaPeca`). */
  base: TaxaDeBase;
  /** A lista inteira nova; vazia é "voltar à de baixo". */
  onMudar: (rates: readonly SteelRate[]) => void;
  /** O tipo para onde a própria pode ir. Só a peça com tipo. */
  aplicarAoTipo?: AplicarTaxaAoTipo | undefined;
  /** A média de hoje do aço do elemento, por bitola (`mediaDoAcoDaPeca`). Vazia ou ausente, nada. */
  media?: ReadonlyMap<SteelGauge, MediaAoLado> | undefined;
  /** Os serviços de aço aprovados que leem esta taxa (`acoAprovadoDoElemento`). Vazio = livre. */
  travadaPor?: readonly AprovacaoDoServico[];
}) {
  const travada = travadaPor.length > 0;
  const rotulo = fala.nome;
  const valendo = propria ?? base.taxas ?? [];
  const kgDe = new Map(valendo.map((taxa) => [taxa.gauge, taxa.kgPerM3]));
  const daBase = new Map((base.taxas ?? []).map((taxa) => [taxa.gauge, taxa.kgPerM3]));
  // A lista do elemento na disciplina que mistura (Fase 42, RN-573) diz de qual é: "a lista do muro".
  // A do tipo que segue a média inteira diz que é da média (Fase 42, etapa 4 — ADR-191).
  const tipoDaMedia =
    base.doTipo !== undefined &&
    base.taxas !== undefined &&
    base.taxas.length > 0 &&
    base.taxas.every((taxa) => taxa.origin === 'MEDIA');
  const origem =
    base.doTipo !== undefined
      ? `do tipo ${base.doTipo}${tipoDaMedia ? ', da média' : ''}`
      : base.disciplina !== undefined
        ? base.porElemento === true
          ? `da disciplina ${base.disciplina}, a lista ${fala.do} ${rotulo}`
          : `da disciplina ${base.disciplina}`
        : 'da disciplina';

  /** A lista inteira com a bitola trocada — a própria nasce da de baixo (ADR-187). */
  const trocar = (gauge: SteelGauge, kg: number | undefined) => {
    const proximas = STEEL_GAUGES.flatMap(({ code }): SteelRate[] => {
      const valor = code === gauge ? kg : kgDe.get(code);
      return valor === undefined ? [] : [{ gauge: code, kgPerM3: valor }];
    });
    onMudar(proximas);
  };

  let explicacao: string;
  if (propria !== undefined) explicacao = `A taxa própria vale inteira, no lugar da ${origem}.`;
  else if (base.taxas !== undefined) {
    // Travada, a própria não nasce: a frase não a oferece.
    explicacao = travada
      ? `Vale a taxa ${origem}.`
      : `Vale a taxa ${origem}. Mudar uma bitola cria a própria ${fala.deste} ${rotulo}, a partir dela.`;
  } else if (base.disciplina !== undefined) {
    explicacao = `A disciplina ${base.disciplina} ainda não tem taxa: sem a própria, o aço ${fala.deste} ${rotulo} não mede.`;
  } else {
    explicacao = `Nenhum serviço da obra mede o aço ${fala.do} ${rotulo}: a taxa própria vale quando houver.`;
  }
  const comMedia = media !== undefined && media.size > 0;
  if (comMedia) {
    explicacao +=
      ' A média ao lado de cada bitola é a das obras marcadas: só para ler, ela não muda a taxa.';
  }

  return (
    <details className="flex flex-col gap-2 text-xs">
      <summary className="cursor-pointer text-sm font-medium">
        Aço {fala.deste} {rotulo} (kg/m³)
        {propria !== undefined && (
          <span className="ml-1 font-normal text-(--color-content-muted)">(própria)</span>
        )}
      </summary>
      <div className="mt-2 flex flex-col gap-2">
        <p className="text-(--color-content-muted)">{explicacao}</p>
        {/* Uma coluna: o card é estreito, e duas quebravam o rótulo da bitola em três linhas. */}
        <div className="grid grid-cols-1 gap-y-1">
          {STEEL_GAUGES.map((info) => {
            const deBaixo = daBase.get(info.code);
            // A média igual à taxa da bitola não se repete, como no fator (`MediaDaLinha`).
            const comMedia = media?.get(info.code);
            const valor = kgDe.get(info.code);
            const daMedia =
              comMedia !== undefined &&
              valor !== undefined &&
              metros(valor, 3) === metros(comMedia.rate, 3)
                ? undefined
                : comMedia;
            return (
              <div key={info.code} className="flex items-center justify-between gap-2">
                <span className="whitespace-nowrap text-(--color-content-muted)">{info.label}</span>
                <span className="flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5">
                  {propria !== undefined && deBaixo !== kgDe.get(info.code) && (
                    <span className="tabular text-(--color-content-muted)">
                      {deBaixo === undefined ? 'era —' : `era ${metros(deBaixo, 3)}`}
                    </span>
                  )}
                  {daMedia !== undefined && (
                    <span
                      className="tabular text-(--color-content-muted)"
                      title={`Média de ${String(daMedia.projects)} ${daMedia.projects === 1 ? 'obra' : 'obras'}`}
                    >
                      média {metros(daMedia.rate, 3)}
                    </span>
                  )}
                  {travada ? (
                    // Só se lê: o número no lugar do campo, como a taxa da disciplina aprovada.
                    <span className="tabular h-8 w-20 py-1.5 text-right">
                      {valor === undefined ? '—' : metros(valor, 3)}
                    </span>
                  ) : (
                    <TaxaAoTerminar
                      rotulo={`Aço de ${info.label} ${fala.neste} ${rotulo}, em kg por m³`}
                      valor={valor}
                      onCommit={(kg) => trocar(info.code, kg)}
                    />
                  )}
                </span>
              </div>
            );
          })}
        </div>
        <span className="tabular text-(--color-content-muted)">
          {metros(totalSteelRate(valendo), 3)} kg/m³ somadas as bitolas
        </span>
        {travada && (
          <p className="flex items-start gap-1.5 text-(--color-content-muted)">
            <Lock className="mt-0.5 size-3.5 shrink-0 text-(--color-state-ok)" aria-hidden="true" />
            <span>
              A taxa não muda mais: {travadaPor.map(nomeDoAprovado).join(', ')}{' '}
              {travadaPor.length === 1 ? 'foi aprovado' : 'foram aprovados'} no orçamento, e{' '}
              {travadaPor.length === 1 ? 'lê' : 'leem'} esta taxa.
            </span>
          </p>
        )}
        {propria !== undefined && !travada && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => onMudar([])}>
              <RotateCcw className="size-4" />{' '}
              {base.doTipo !== undefined
                ? `Voltar à do tipo ${base.doTipo}`
                : 'Voltar à da disciplina'}
            </Button>
            {aplicarAoTipo !== undefined && (
              <Button
                variant="link"
                size="sm"
                className="h-auto px-0 text-xs md:h-auto"
                onClick={() => aplicarAoTipo.onAplicar(propria)}
              >
                Aplicar ao tipo {aplicarAoTipo.nome}
              </Button>
            )}
          </div>
        )}
      </div>
    </details>
  );
}

/**
 * O kg/m³ de uma bitola: grava ao sair do campo ou no Enter, e não a cada tecla — o "0," do
 * caminho até 0,5 seria uma bitola tirada no meio da digitação. Vazio tira a bitola.
 */
function TaxaAoTerminar({
  rotulo,
  valor,
  onCommit,
}: {
  rotulo: string;
  valor: number | undefined;
  onCommit: (kg: number | undefined) => void;
}) {
  const [texto, setTexto] = useState(valor === undefined ? '' : paraTexto(valor));
  const [erro, setErro] = useState<string | undefined>(undefined);

  useEffect(() => {
    setTexto(valor === undefined ? '' : paraTexto(valor));
  }, [valor]);

  const aplicar = () => {
    if (texto.trim() === '') {
      setErro(undefined);
      if (valor !== undefined) onCommit(undefined);
      return;
    }
    const kg = paraNumero(texto);
    if (kg === undefined || !(kg > 0)) {
      setErro('A taxa deve ser maior que zero.');
      return;
    }
    setErro(undefined);
    if (kg !== valor) onCommit(kg);
  };

  return (
    <span className="flex flex-col items-end gap-0.5">
      <Input
        aria-label={rotulo}
        inputMode="decimal"
        value={texto}
        placeholder="—"
        aria-invalid={erro !== undefined}
        className="tabular h-8 w-20 text-right"
        onChange={(evento) => setTexto(evento.target.value)}
        onBlur={aplicar}
        onKeyDown={(evento) => {
          if (evento.key === 'Enter') aplicar();
        }}
      />
      {erro !== undefined && (
        <span role="alert" className="text-(--color-state-danger)">
          {erro}
        </span>
      )}
    </span>
  );
}

/**
 * O campo "Tipo" do card do bloco (Fase 41, RN-536, RN-537): escolhe um tipo da obra na lista, ou
 * digita um nome novo — e o tipo nasce ali (`ADD_PILE_CAP_TYPE`), já dado a este bloco. Vazio
 * tira o tipo: o bloco volta a seguir a obra. Grava ao sair do campo ou no Enter.
 *
 * Sem tela à parte para o cadastro (pedido do Gilvan, 27/10/2026): a composição do tipo são as
 * linhas "do tipo" dos fatores logo abaixo, e renomear ou excluir o tipo ficam de fora.
 *
 * Desde a Fase 42 (etapa 4 — G11, RN-582), o tipo novo nasce com a média da fundação: cada item
 * dos serviços do bloco com média ganha o fator médio, e o tipo a taxa média do aço do bloco, tudo
 * "da média" (`mediaDoTipoNovo`). Sem média, ele nasce vazio, como antes.
 */
function CampoDoTipoDoBloco({
  bloco,
  tipos,
  projectId,
  catalogo,
  media,
  travado,
  dispatch,
}: {
  bloco: PileCap;
  tipos: readonly PileCapType[];
  projectId: ProjectId | undefined;
  catalogo: Pick<ServiceCatalog, 'disciplines'> | undefined;
  /** A média das obras marcadas: o tipo novo nasce com a da fundação (RN-582). */
  media: ApartmentAverageForProject | undefined;
  /** O serviço aprovado: o tipo novo não ganha linha dele (RN-566). */
  travado: (serviceKey: string) => boolean;
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  const tipo = tipos.find((candidato) => candidato.id === bloco.pileCapTypeId);
  const [texto, setTexto] = useState(tipo?.name ?? '');
  const [erro, setErro] = useState<string | undefined>(undefined);
  const id = `bloco-${bloco.id}-tipo`;

  useEffect(() => {
    setTexto(tipo?.name ?? '');
  }, [tipo?.name]);

  const aplicar = (): void => {
    const nome = texto.trim();
    setErro(undefined);
    if (nome === '') {
      if (tipo !== undefined) dispatch({ type: 'SET_PILE_CAP_TYPE_OF', pieceIds: [bloco.id] });
      return;
    }
    const existente = tipos.find(
      (candidato) =>
        candidato.deletedAt === undefined && normalizeName(candidato.name) === normalizeName(nome),
    );
    if (existente !== undefined) {
      setTexto(existente.name);
      if (existente.id !== tipo?.id) {
        dispatch({ type: 'SET_PILE_CAP_TYPE_OF', pieceIds: [bloco.id], typeId: existente.id });
      }
      return;
    }
    // O tipo novo: a conferência do domínio antes, para a recusa ficar no campo e o nome
    // digitado não se perder; depois o tipo nasce e já vai para o bloco.
    const conferido = checkPileCapTypeFields({ name: nome, rates: [] }, tipos);
    if (!conferido.ok) {
      setErro(conferido.error.message);
      return;
    }
    const novo = asId<'PileCapType'>(crypto.randomUUID());
    dispatch({
      type: 'ADD_PILE_CAP_TYPE',
      name: conferido.value.name,
      id: novo,
      ...(projectId === undefined ? {} : { projectId }),
    });
    // O tipo nasce com a média da fundação (RN-582), antes de ir para o bloco.
    const daMedia = mediaDoTipoNovo(catalogo, 'BLOCO', media, travado);
    if (!semNadaNoTipo(daMedia)) {
      dispatch({ type: 'APPLY_PILE_CAP_TYPE_AVERAGE', typeId: novo, ...daMedia });
    }
    dispatch({ type: 'SET_PILE_CAP_TYPE_OF', pieceIds: [bloco.id], typeId: novo });
  };

  return (
    <div className="flex flex-col gap-1">
      <Field label="Tipo" htmlFor={id}>
        <Input
          id={id}
          list={`${id}-lista`}
          value={texto}
          maxLength={PILE_CAP_TYPE_NAME_MAX}
          placeholder="sem tipo — segue a obra"
          className="w-48"
          aria-invalid={erro !== undefined}
          onChange={(evento) => setTexto(evento.target.value)}
          onBlur={aplicar}
          onKeyDown={(evento) => {
            if (evento.key === 'Enter') aplicar();
          }}
        />
      </Field>
      <datalist id={`${id}-lista`}>
        {tipos
          .filter((candidato) => candidato.deletedAt === undefined)
          .map((candidato) => (
            <option key={candidato.id} value={candidato.name} />
          ))}
      </datalist>
      <span className="text-xs text-(--color-content-muted)">
        {tipo === undefined
          ? 'Escolha um tipo da obra ou digite um nome novo. Sem tipo, o bloco segue o índice da obra.'
          : `Os fatores "do tipo" abaixo são a composição do ${tipo.name}, para todo bloco dele.`}
      </span>
      {erro !== undefined && (
        <span role="alert" className="text-xs text-(--color-state-danger)">
          {erro}
        </span>
      )}
    </div>
  );
}

/**
 * "Aplicar o BL2E a outros blocos" (Fase 41, RN-537): dar o tipo deste bloco a vários de uma vez,
 * dentro do próprio card — a prancha não tem seleção de vários blocos. Fechado por padrão. O
 * bloco da planilha por peça não se marca (RN-540).
 */
function AplicarOTipoAOutros({
  bloco,
  tipo,
  blocos,
  importadas,
  dispatch,
}: {
  bloco: PileCap;
  tipo: PileCapType;
  blocos: readonly PileCap[];
  importadas: ReadonlySet<string>;
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  const [marcados, setMarcados] = useState<ReadonlySet<string>>(new Set());
  const outros = blocos.filter(
    (outro) =>
      outro.id !== bloco.id && outro.deletedAt === undefined && outro.pileCapTypeId !== tipo.id,
  );
  const marcaveis = outros.filter((outro) => !importadas.has(nomeDaPeca(outro.name)));
  if (outros.length === 0) return null;
  const escolhidos = marcaveis.filter((outro) => marcados.has(outro.id));

  return (
    <details className="flex flex-col gap-2 text-xs">
      <summary className="cursor-pointer text-sm font-medium">
        Aplicar o {tipo.name} a outros blocos
      </summary>
      <ul aria-label={`Blocos sem o ${tipo.name}`} className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
        {outros.map((outro) => {
          const daPlanilha = importadas.has(nomeDaPeca(outro.name));
          const id = `bloco-${bloco.id}-aplicar-${outro.id}`;
          return (
            <li key={outro.id} className="flex items-center gap-1">
              <input
                id={id}
                type="checkbox"
                className="size-4"
                disabled={daPlanilha}
                checked={marcados.has(outro.id)}
                onChange={(evento) => {
                  const novos = new Set(marcados);
                  if (evento.target.checked) novos.add(outro.id);
                  else novos.delete(outro.id);
                  setMarcados(novos);
                }}
              />
              <label
                htmlFor={id}
                {...(daPlanilha ? { title: 'Veio da planilha por peça: não leva tipo.' } : {})}
              >
                {outro.name}
              </label>
            </li>
          );
        })}
      </ul>
      <div className="mt-1 flex flex-wrap gap-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={marcaveis.length === 0}
          onClick={() =>
            setMarcados(
              escolhidos.length === marcaveis.length
                ? new Set()
                : new Set(marcaveis.map((outro) => String(outro.id))),
            )
          }
        >
          {escolhidos.length === marcaveis.length && marcaveis.length > 0
            ? 'Desmarcar todos'
            : 'Marcar todos'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={escolhidos.length === 0}
          onClick={() => {
            dispatch({
              type: 'SET_PILE_CAP_TYPE_OF',
              pieceIds: escolhidos.map((outro) => outro.id),
              typeId: tipo.id,
            });
            setMarcados(new Set());
          }}
        >
          Aplicar o {tipo.name}
          {escolhidos.length > 0 && ` a ${String(escolhidos.length)}`}
        </Button>
      </div>
    </details>
  );
}

/**
 * Editar o bloco clicado: o nome e a altura, com as contas dele à vista, o tipo e os fatores
 * próprios (Fase 41, RN-537, RN-538), e excluir.
 */
export function BlocoSelecionado({
  bloco,
  erro,
  blocos = [],
  tipos = [],
  projectId,
  catalogo,
  pecasDaPlanilha,
  media,
  aprovados,
  dispatch: despachar,
  onFechar,
}: {
  bloco: PileCap;
  /** A última recusa do desenho — só aparece depois de a pessoa editar **este** bloco. */
  erro: string | undefined;
  /** Os blocos do desenho — o "aplicar a outros blocos" do tipo (RN-537). */
  blocos?: readonly PileCap[];
  /** Os tipos de bloco da obra (Fase 41, RN-536). */
  tipos?: readonly PileCapType[];
  /** A obra do tipo que nasce no campo. Ausente, a dos tipos que já existem. */
  projectId?: ProjectId;
  /** O cadastro da obra, para a lista de fatores (RN-538). Ausente, a lista não aparece. */
  catalogo?: Pick<ServiceCatalog, 'disciplines'> | undefined;
  /** A planilha por peça da tipologia: o bloco de nome importado não leva tipo nem fator. */
  pecasDaPlanilha?: QuantidadesPorPeca | undefined;
  /**
   * A média das obras marcadas (`useMediaDoApartamento`): a da fundação aparece ao lado do fator
   * e de cada bitola, só para ler (Fase 41, RN-554). Desde a Fase 42 (etapa 4 — G11, RN-582,
   * RN-583), o tipo novo nasce com ela, e a linha do tipo que a segue mostra "A média mudou".
   * Ausente, o card é o de antes.
   */
  media?: ApartmentAverageForProject | undefined;
  /**
   * Os serviços aprovados (Fase 33): o tipo novo não ganha linha deles, e o "Atualizar" não os
   * toca (RN-566). Ausente, nada trava na tela — a bancada e o servidor seguram o que passar.
   */
  aprovados?: ServicosAprovados | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
  onFechar: () => void;
}) {
  const [editou, setEditou] = useState(false);
  const dispatch: React.Dispatch<WorkspaceAction> = (acao) => {
    setEditou(true);
    despachar(acao);
  };
  const importadas = useMemo(() => pecasImportadas(pecasDaPlanilha), [pecasDaPlanilha]);
  const daPlanilha = importadas.has(nomeDaPeca(bloco.name));
  const tipo = tipos.find((candidato) => candidato.id === bloco.pileCapTypeId);
  const fatores = useMemo(
    () => fatoresDoBloco(catalogo, bloco, tipo, media),
    [catalogo, bloco, tipo, media],
  );
  const mediaDoAco = useMemo(() => mediaDoAcoDaPeca(media, 'BLOCO'), [media]);
  const travado = (serviceKey: string): boolean =>
    aprovados !== undefined && aprovacaoDe(aprovados, serviceKey) !== undefined;
  // A linha do tipo que segue a média e mudou (Fase 42, RN-583).
  const mudancas =
    tipo === undefined ? undefined : mudancasDoTipo(catalogo, 'BLOCO', tipo, media, travado);

  return (
    <section
      aria-label={`Bloco ${bloco.name}`}
      className="flex flex-col gap-2 rounded-md border border-(--color-brand) p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Bloco {bloco.name}</h3>
        <Button variant="ghost" size="sm" onClick={onFechar}>
          Fechar
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Field label="Nome" htmlFor={`bloco-${bloco.id}-nome`}>
          <NomeInput
            id={`bloco-${bloco.id}-nome`}
            value={bloco.name}
            className="w-28"
            respostaImediata
            onDescartar={() => dispatch({ type: 'DISMISS_ERROR' })}
            onCommit={(nome) =>
              dispatch({ type: 'UPDATE_PILE_CAP', pieceId: bloco.id, name: nome })
            }
          />
        </Field>
        <Field label="Altura (m)" htmlFor={`bloco-${bloco.id}-altura`}>
          <NumeroAoTerminar
            id={`bloco-${bloco.id}-altura`}
            valor={bloco.height}
            className="w-24"
            onCommit={(altura) =>
              dispatch({ type: 'UPDATE_PILE_CAP', pieceId: bloco.id, height: altura })
            }
          />
        </Field>
      </div>

      <div className="flex flex-col gap-1 text-xs">
        <Linha rotulo="Área em planta" valor={`${metros(pileCapBottom(bloco))} m²`} />
        <Linha rotulo="Concreto" valor={`${metros(pileCapVolume(bloco), 3)} m³`} />
      </div>

      {/* O tipo de bloco (RN-537): a composição dele vale no bloco, e o próprio por cima. */}
      {daPlanilha ? (
        bloco.pileCapTypeId !== undefined && (
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dispatch({ type: 'SET_PILE_CAP_TYPE_OF', pieceIds: [bloco.id] })}
            >
              Tirar o tipo {tipo?.name ?? ''}
            </Button>
          </div>
        )
      ) : (
        <>
          <CampoDoTipoDoBloco
            bloco={bloco}
            tipos={tipos}
            projectId={projectId}
            catalogo={catalogo}
            media={media}
            travado={travado}
            dispatch={dispatch}
          />
          {tipo !== undefined && mudancas !== undefined && (
            <AMediaDoTipoMudou
              tipo={tipo.name}
              paraQuem="todo bloco dele"
              mudancas={mudancas}
              onAtualizar={() =>
                dispatch({
                  type: 'APPLY_PILE_CAP_TYPE_AVERAGE',
                  typeId: tipo.id,
                  ...atualizarTipoPelaMedia(catalogo, 'BLOCO', tipo, media, travado),
                })
              }
            />
          )}
          {tipo !== undefined && (
            <AplicarOTipoAOutros
              bloco={bloco}
              tipo={tipo}
              blocos={blocos}
              importadas={importadas}
              dispatch={dispatch}
            />
          )}
        </>
      )}

      {catalogo !== undefined && (
        <FatoresDaPecaDaFundacao
          peca={bloco}
          rotulo="bloco"
          servicos={fatores}
          daPlanilha={daPlanilha}
          semFator="não leva tipo nem fator próprio"
          rotuloDaVolta={tipo === undefined ? 'Voltar ao índice' : 'Voltar ao do tipo'}
          dispatch={dispatch}
          // O próprio deste bloco vira a composição do tipo, e o bloco volta a lê-la (RN-536).
          aplicarAoTipo={
            tipo === undefined
              ? undefined
              : {
                  nome: tipo.name,
                  onAplicar: (serviceKey, itemKey, factor) => {
                    dispatch({
                      type: 'SET_PILE_CAP_TYPE_RATE',
                      typeId: tipo.id,
                      serviceKey,
                      itemKey,
                      factor,
                    });
                    dispatch({
                      type: 'SET_FOUNDATION_FACTOR',
                      pieceId: bloco.id,
                      serviceKey,
                      itemKey,
                    });
                  },
                }
          }
        />
      )}
      {catalogo !== undefined && (
        <AcoDaPecaDaFundacao
          peca={bloco}
          rotulo="bloco"
          base={taxaDeBaseDaPeca(catalogo, 'BLOCO', tipo)}
          daPlanilha={daPlanilha}
          dispatch={dispatch}
          media={mediaDoAco}
          // A própria deste bloco vira a taxa do tipo, e o bloco volta a lê-la (RN-543).
          aplicarAoTipo={
            tipo === undefined
              ? undefined
              : {
                  nome: tipo.name,
                  onAplicar: (taxas) => {
                    dispatch({
                      type: 'SET_PILE_CAP_TYPE_STEEL_RATES',
                      typeId: tipo.id,
                      rates: taxas,
                    });
                    dispatch({ type: 'SET_PIECE_STEEL_RATES', pieceId: bloco.id, rates: [] });
                  },
                }
          }
        />
      )}
      {catalogo === undefined && daPlanilha && (
        <p className="text-xs text-(--color-content-muted)">
          Este bloco veio pronto da planilha por peça: a quantidade dele já vem pronta, e ele não
          leva tipo nem fator próprio.
        </p>
      )}

      {editou && erro !== undefined && (
        <p role="alert" className="text-xs text-(--color-state-danger)">
          {erro}
        </p>
      )}

      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            dispatch({ type: 'REMOVE_PILE_CAP', pieceId: bloco.id });
            onFechar();
          }}
        >
          <Trash2 className="size-4" /> Excluir bloco
        </Button>
      </div>
    </section>
  );
}

/**
 * Editar o baldrame clicado: o nome e a seção, o eixo e o comprimento livre à vista, e
 * excluir. As pontas se movem arrastando as alças na prancha.
 */
export function BaldrameSelecionado({
  baldrame,
  blocos,
  erro,
  catalogo,
  pecasDaPlanilha,
  media,
  dispatch: despachar,
  onFechar,
}: {
  baldrame: GroundBeam;
  blocos: readonly PileCap[];
  /** A última recusa do desenho — só aparece depois de a pessoa editar **este** baldrame. */
  erro: string | undefined;
  /** O cadastro da obra, para os fatores próprios (Fase 41, RN-538). Ausente, sem a lista. */
  catalogo?: Pick<ServiceCatalog, 'disciplines'> | undefined;
  /** A planilha por peça: o baldrame de nome importado não leva fator (RN-540). */
  pecasDaPlanilha?: QuantidadesPorPeca | undefined;
  /** A média das obras marcadas, ao lado do fator e da bitola (RN-554) — ver `BlocoSelecionado`. */
  media?: ApartmentAverageForProject | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
  onFechar: () => void;
}) {
  const [editou, setEditou] = useState(false);
  const dispatch: React.Dispatch<WorkspaceAction> = (acao) => {
    setEditou(true);
    despachar(acao);
  };
  const livre = useMemo(() => groundBeamFreeLength(baldrame, blocos), [baldrame, blocos]);
  const fatores = useMemo(
    () => fatoresDaPecaDaFundacao(catalogo, 'BALDRAME', baldrame, media),
    [catalogo, baldrame, media],
  );
  const mediaDoAco = useMemo(() => mediaDoAcoDaPeca(media, 'BALDRAME'), [media]);

  return (
    <section
      aria-label={`Baldrame ${baldrame.name}`}
      className="flex flex-col gap-2 rounded-md border border-(--color-brand) p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Baldrame {baldrame.name}</h3>
        <Button variant="ghost" size="sm" onClick={onFechar}>
          Fechar
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Field label="Nome" htmlFor={`baldrame-${baldrame.id}-nome`}>
          <NomeInput
            id={`baldrame-${baldrame.id}-nome`}
            value={baldrame.name}
            className="w-28"
            respostaImediata
            onDescartar={() => dispatch({ type: 'DISMISS_ERROR' })}
            onCommit={(nome) =>
              dispatch({ type: 'UPDATE_GROUND_BEAM', pieceId: baldrame.id, name: nome })
            }
          />
        </Field>
        <Field label="Largura (cm)" htmlFor={`baldrame-${baldrame.id}-largura`}>
          <NumeroAoTerminar
            id={`baldrame-${baldrame.id}-largura`}
            valor={emCentimetros(baldrame.width)}
            className="w-24"
            onCommit={(largura) =>
              dispatch({
                type: 'UPDATE_GROUND_BEAM',
                pieceId: baldrame.id,
                width: largura / 100,
              })
            }
          />
        </Field>
        <Field label="Altura (cm)" htmlFor={`baldrame-${baldrame.id}-altura`}>
          <NumeroAoTerminar
            id={`baldrame-${baldrame.id}-altura`}
            valor={emCentimetros(baldrame.height)}
            className="w-24"
            onCommit={(altura) =>
              dispatch({
                type: 'UPDATE_GROUND_BEAM',
                pieceId: baldrame.id,
                height: altura / 100,
              })
            }
          />
        </Field>
      </div>

      <div className="flex flex-col gap-1 text-xs">
        <Linha rotulo="Seção" valor={`${secaoDoBaldrame(baldrame)} cm`} />
        <Linha rotulo="Eixo, de ponta a ponta" valor={`${metros(comprimentoDoEixo(baldrame))} m`} />
        <Linha rotulo="Livre, fora dos blocos" valor={`${metros(livre)} m`} />
        <Linha
          rotulo="Concreto do trecho livre"
          valor={`${metros(groundBeamVolume(baldrame, livre), 3)} m³`}
        />
      </div>
      <p className="text-xs text-(--color-content-muted)">
        Arraste uma alça da ponta para mover só ela; solta dentro de um bloco, ela vai para o centro
        dele.
      </p>

      {catalogo !== undefined && (
        <FatoresDaPecaDaFundacao
          peca={baldrame}
          rotulo="baldrame"
          servicos={fatores}
          daPlanilha={pecasImportadas(pecasDaPlanilha).has(nomeDaPeca(baldrame.name))}
          semFator="não leva fator próprio"
          rotuloDaVolta="Voltar ao índice"
          dispatch={dispatch}
        />
      )}
      {catalogo !== undefined && (
        <AcoDaPecaDaFundacao
          peca={baldrame}
          rotulo="baldrame"
          base={taxaDeBaseDaPeca(catalogo, 'BALDRAME')}
          daPlanilha={pecasImportadas(pecasDaPlanilha).has(nomeDaPeca(baldrame.name))}
          dispatch={dispatch}
          media={mediaDoAco}
        />
      )}

      {editou && erro !== undefined && (
        <p role="alert" className="text-xs text-(--color-state-danger)">
          {erro}
        </p>
      )}

      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            dispatch({ type: 'REMOVE_GROUND_BEAM', pieceId: baldrame.id });
            onFechar();
          }}
        >
          <Trash2 className="size-4" /> Excluir baldrame
        </Button>
      </div>
    </section>
  );
}
